import React from "react";
import {
  Alert, Button, Chip, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import { useOutletContext } from "react-router-dom";
import { realEstateApi } from "../../../api/realEstate";
import { formatApiError } from "../../../api/client";
import RealEstatePageHeader from "../../../components/dashboard/realEstate/RealEstatePageHeader";
import PropertyDialog from "../../../components/dashboard/realEstate/PropertyDialog";
import DashboardLoadingState from "../../../components/dashboard/DashboardLoadingState";

const STATUS = { available: "Disponible", reserved: "Reservada", rented: "Alquilada", sold: "Vendida", inactive: "Inactiva" };
const OPERATION = { rent: "Alquiler", sale: "Venta", both: "Venta / alquiler" };
const PUBLIC_PROPERTY_STATUSES = new Set(["available", "reserved"]);
const isEffectivelyPublic = (property) => property?.publicVisible === true && PUBLIC_PROPERTY_STATUSES.has(property?.status);
const money = (value, currency = "ARS") => new Intl.NumberFormat("es-AR", { style: "currency", currency, maximumFractionDigits: 0 }).format(Number(value || 0));

export default function ProjectPropertiesPage() {
  const { projectId, canManageProperties } = useOutletContext();
  const [search, setSearch] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(20);
  const [data, setData] = React.useState({ properties: [], pagination: { total: 0 } });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [dialog, setDialog] = React.useState({ open: false, property: null });
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => { const timer = setTimeout(() => { setQuery(search.trim()); setPage(0); }, 300); return () => clearTimeout(timer); }, [search]);
  const load = React.useCallback(async () => {
    setLoading(true); setError("");
    try { setData(await realEstateApi.listProperties(projectId, { page: page + 1, pageSize, search: query || undefined })); }
    catch (err) { setError(formatApiError(err, "No se pudieron cargar las propiedades.")); }
    finally { setLoading(false); }
  }, [projectId, page, pageSize, query]);
  React.useEffect(() => { load(); }, [load]);

  const save = async (payload) => {
    setSaving(true); setError("");
    try {
      if (dialog.property) await realEstateApi.updateProperty(projectId, dialog.property._id || dialog.property.id, payload);
      else await realEstateApi.createProperty(projectId, payload);
      setDialog({ open: false, property: null }); await load();
    } catch (err) { setError(formatApiError(err, "No se pudo guardar la propiedad.")); }
    finally { setSaving(false); }
  };
  const deactivate = async (property) => {
    const propertyTitle = property.title || "esta propiedad";
    if (!window.confirm(`¿Desactivar «${propertyTitle}»?\n\nLa propiedad quedará inactiva y dejará de publicarse. Su información y su historial se conservarán.`)) return;
    try { await realEstateApi.deleteProperty(projectId, property._id || property.id); await load(); }
    catch (err) { setError(formatApiError(err, "No se pudo desactivar la propiedad.")); }
  };

  return (
    <Stack spacing={2.5}>
      <RealEstatePageHeader title="Propiedades" description="Catálogo interno y publicación pública de inmuebles." actionLabel={canManageProperties ? "Nueva propiedad" : ""} onAction={canManageProperties ? () => setDialog({ open: true, property: null }) : null} />
      {error ? <Alert severity="error">{error}</Alert> : null}
      <Paper sx={{ p: 2, borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        <TextField fullWidth value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Buscar por título, código, tipo o ubicación..." size="small" />
      </Paper>
      <TableContainer component={Paper} sx={{ borderRadius: 3, border: "1px solid rgba(var(--wp-panel-ink-rgb, 25,36,47),.08)" }}>
        {loading ? <DashboardLoadingState label="Cargando propiedades…" minHeight={280} /> : (
          <Table size="small">
            <TableHead><TableRow><TableCell>Propiedad</TableCell><TableCell>Operación</TableCell><TableCell>Estado</TableCell><TableCell>Precio</TableCell><TableCell>Publicación</TableCell><TableCell align="right">Acciones</TableCell></TableRow></TableHead>
            <TableBody>
              {(data.properties || []).map((property) => <TableRow key={property._id || property.id} hover>
                <TableCell><Typography sx={{ fontWeight: 900 }}>{property.title}</Typography><Typography variant="caption" color="text.secondary">{[property.code, property.propertyType, property.address?.city, property.address?.province].filter(Boolean).join(" · ") || "Sin ubicación cargada"}</Typography></TableCell>
                <TableCell>{OPERATION[property.operation] || property.operation}</TableCell>
                <TableCell><Chip size="small" label={STATUS[property.status] || property.status} /></TableCell>
                <TableCell sx={{ fontWeight: 800 }}>{money(property.price, property.currency)}</TableCell>
                <TableCell>{isEffectivelyPublic(property)
                  ? <Chip size="small" icon={<PublicRoundedIcon />} label="Publicada" color="success" variant="outlined" />
                  : property.publicVisible === true
                    ? <Chip size="small" label="Oculta por estado" color="warning" variant="outlined" />
                    : <Chip size="small" label="Privada" variant="outlined" />}</TableCell>
                <TableCell align="right">{canManageProperties ? <><Tooltip title="Editar"><IconButton onClick={() => setDialog({ open: true, property })}><EditRoundedIcon /></IconButton></Tooltip>{property.status !== "inactive" ? <Tooltip title="Desactivar propiedad"><IconButton aria-label={`Desactivar propiedad ${property.title || ""}`.trim()} onClick={() => deactivate(property)}><DeleteOutlineRoundedIcon /></IconButton></Tooltip> : null}</> : "—"}</TableCell>
              </TableRow>)}
              {!data.properties?.length ? <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5, color: "text.secondary" }}>No hay propiedades para este filtro.</TableCell></TableRow> : null}
            </TableBody>
          </Table>
        )}
        <TablePagination component="div" count={data.pagination?.total || 0} page={page} onPageChange={(_event, value) => setPage(value)} rowsPerPage={pageSize} onRowsPerPageChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 20, 50, 100]} labelRowsPerPage="Filas" />
      </TableContainer>
      <PropertyDialog open={dialog.open} property={dialog.property} onClose={() => setDialog({ open: false, property: null })} onSave={save} saving={saving} />
    </Stack>
  );
}
